"use client";

import { AlertOutlined, CheckCircleFilled } from "@ant-design/icons";
import type React from "react";
import { useState } from "react";
import { FaTrash } from "react-icons/fa";

interface MealCategoryProps {
  data: { _id: string; name: string }[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onDeleteMeal: (id: string) => Promise<void>;
}

const MealCategory: React.FC<MealCategoryProps> = ({
  data,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
  onDeleteMeal,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this meal?"
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(id);
      const response = await onDeleteMeal(id);
      setSuccessMessage("Meal deleted successfully!");
      setErrorMessage(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "An error occurred.";
      setErrorMessage(message);
      setSuccessMessage(null);
    } finally {
      setDeletingId(null);
      setTimeout(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
      }, 3000);
    }
  };

  return (
    <div className="w-full bg-white rounded-lg shadow-sm overflow-hidden border border-zinc-200">
      <div className="p-4 sm:p-6 border-b border-zinc-200 bg-amber-50">
        <h3 className="text-xl font-semibold text-zinc-800">Meal Categories</h3>
        <p className="text-sm text-zinc-500 mt-1">
          Manage your meal categories
        </p>
      </div>

      {(errorMessage || successMessage) && (
        <div
          className={`p-3 mx-4 mt-4 rounded-md flex items-center gap-2 ${
            errorMessage
              ? "bg-red-50 text-red-700"
              : "bg-green-50 text-green-700"
          }`}
        >
          {errorMessage ? (
            <AlertOutlined className="h-5 w-5 flex-shrink-0" />
          ) : (
            <CheckCircleFilled className="h-5 w-5 flex-shrink-0" />
          )}
          <p className="text-sm font-medium">
            {errorMessage || successMessage}
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-3 bg-amber-200 border-b border-zinc-300 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 bg-amber-200 border-b border-zinc-300 text-right text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {data.map((category) => (
              <tr
                key={category._id}
                className="hover:bg-zinc-50 transition-colors duration-150"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-800">
                  {category.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <button
                    onClick={() => handleDelete(category._id)}
                    disabled={deletingId === category._id}
                    className={`inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                      deletingId === category._id
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <FaTrash className="h-4 w-4" />
                    {deletingId === category._id ? "Deleting..." : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={2} className="px-6 py-10 text-center">
                  <div className="flex flex-col items-center justify-center text-zinc-500">
                    <p className="mt-2 text-sm">
                      No meal categories available.
                    </p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Add a new category to get started
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-3 flex items-center justify-between border-t border-zinc-200 bg-zinc-50">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-zinc-300 text-sm font-medium rounded-md text-zinc-700 bg-white hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-zinc-300 text-sm font-medium rounded-md text-zinc-700 bg-white hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-zinc-700">
                Showing{" "}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * itemsPerPage + 1, data.length)}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, data.length)}
                </span>{" "}
                of <span className="font-medium">{data.length}</span> results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => onPageChange(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === index + 1
                        ? "z-10 bg-amber-200 border-amber-300 text-zinc-800"
                        : "bg-white border-zinc-300 text-zinc-500 hover:bg-zinc-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealCategory;
